import { useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import DateTime from "components/DateTime";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Dialog from "components/DesignComponents/Dialog";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { body1BoldStyles, body3SemiBold } from "shared/styles/typography";

import { useNewsTracking } from "../hooks/useNewsTracking";
import { NewsDialogProps } from "./types";

const NewsDialog = ({
    isOpen,
    data,
    onClose,
    handleSelectedNews
}: NewsDialogProps) => {
    const { gray100, gray700, textPlaceholder } = colors;

    const { id, image, createdAt, body, primaryButton } = data;

    const { t } = useTranslation();
    const { fromMd } = useMediaQueries();
    const { trackOpen, trackClose, trackScroll } = useNewsTracking(id);

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        trackOpen();

        return () => trackClose();
    }, [isOpen, trackOpen, trackClose]);

    const title = t(`Dashboard##news##title##${id}`);

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator
            title={title}
            open={isOpen}
            close={onClose}
            onScroll={e => trackScroll(e.currentTarget)}
            childrenRef={contentRef}
            actions={
                <>
                    <Button
                        fullWidth
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        onClick={onClose}
                    >
                        {t("Button##close")}
                    </Button>

                    {primaryButton && (
                        <Button
                            fullWidth
                            color={BUTTON_COLORS.Primary}
                            size={BUTTON_SIZES.Normal}
                            variant={BUTTON_VARIANTS.TextOnly}
                            onClick={primaryButton.onAction}
                        >
                            {primaryButton.text}
                        </Button>
                    )}
                </>
            }
            slotProps={{
                transition: {
                    onEntered: () => {
                        if (contentRef.current) {
                            trackScroll(contentRef.current);
                        }
                    },
                    onExited: () => handleSelectedNews(null)
                }
            }}
            extendToMaxWidth
            customPaperStyle={{
                ...(fromMd && {
                    height: "560px"
                })
            }}
        >
            <div
                css={css({
                    display: "flex",
                    columnGap: "16px",

                    "@media(max-width: 599px)": {
                        flexDirection: "column"
                    }
                })}
            >
                <div
                    css={css({
                        width: "193px",
                        height: "128px",

                        "@media(max-width: 599px)": {
                            marginBottom: "16px"
                        },

                        "& > img": {
                            width: "100%"
                        }
                    })}
                >
                    {image ? <img src={image} alt={title} /> : null}
                </div>

                <div css={css({ flex: "1" })}>
                    <div
                        css={[
                            body3SemiBold,
                            { color: textPlaceholder, marginBottom: "8px" }
                        ]}
                    >
                        {t("Dashboard##published")}{" "}
                        <DateTime date={createdAt} format="L" />
                    </div>

                    <div
                        css={css({
                            color: gray700,
                            fontSize: "14px",
                            lineHeight: "20px",
                            letterSpacing: "0.1px",

                            h3: {
                                ...body1BoldStyles,
                                color: gray700,
                                margin: "0"
                            },

                            "ol, p": {
                                margin: "0"
                            },

                            "ul, ol": {
                                paddingLeft: "24px"
                            },

                            li: {
                                listStyle: "revert"
                            },

                            strong: {
                                fontWeight: "600"
                            },

                            code: {
                                background: gray100,
                                borderRadius: "1px",
                                padding: "2px",
                                fontFamily: '"Fira Code", monospace',
                                fontSize: "12px",
                                lineHeight: "16px",
                                letterSpacing: "0.4px"
                            },

                            "section + section": {
                                marginTop: "16px"
                            },

                            "p + p, li + li, p + ul, ul + p, p + ol, ol + p": {
                                marginTop: "8px"
                            }
                        })}
                    >
                        {body}
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default NewsDialog;
